"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface TextareaProps
  extends Omit<React.ComponentProps<"textarea">, "size"> {
  /** Label text displayed above the textarea */
  label?: string;
  /** Helper text displayed below the textarea */
  helperText?: string;
  /** When true, shows "(optional)" after the label. Required is the default. */
  optional?: boolean;
  /** When true, displays error styling */
  isError?: boolean;
  /** Error message that replaces helperText when isError is true */
  errorMessage?: string;
  /** Size variant of the textarea */
  size?: "sm" | "md" | "lg";
  /** Additional class name for the wrapper element */
  wrapperClassName?: string;
  /** Controls the resize behavior of the textarea */
  resize?: "none" | "vertical" | "horizontal" | "both";
  /** Show a character counter when maxLength is set. Defaults to true. */
  showCharacterCount?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      helperText,
      optional = false,
      isError = false,
      errorMessage,
      size = "md",
      disabled,
      id,
      wrapperClassName,
      resize = "vertical",
      rows = 4,
      showCharacterCount = true,
      maxLength,
      value,
      ...props
    },
    ref
  ) => {
    // Generate a unique ID for accessibility if not provided
    const generatedId = React.useId();
    const textareaId = id || generatedId;
    const helperId = `${textareaId}-helper`;
    const hasHelperOrError = !!(helperText || (isError && errorMessage));

    // Character count logic
    const charCount = typeof value === "string" ? value.length : 0;
    const showCounter = showCharacterCount && maxLength != null;
    const charRatio = maxLength ? charCount / maxLength : 0;
    const counterColor =
      charRatio >= 1
        ? "text-error-700 font-medium"
        : charRatio >= 0.9
          ? "text-warning-700"
          : "text-text-secondary";

    // Size-specific classes (simpler than Input since no icons)
    const sizeClasses = {
      sm: {
        textarea: "px-(--input-sm-padding-x) py-(--input-sm-padding-y) body-xs-regular",
      },
      md: {
        textarea: "px-(--input-md-padding-x) py-(--input-md-padding-y) body-sm-regular",
      },
      lg: {
        textarea: "px-(--input-lg-padding-x) py-(--input-lg-padding-y) body-md-regular",
      },
    };

    const currentSize = sizeClasses[size];

    // Resize classes
    const resizeClasses = {
      none: "resize-none",
      vertical: "resize-y",
      horizontal: "resize-x",
      both: "resize",
    };

    return (
      <div className={cn("flex flex-col gap-1", wrapperClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              "body-sm-medium text-(--input-label)",
              disabled && "opacity-50"
            )}
          >
            {label}
            {optional && (
              <span className="text-(--input-label-optional) body-sm-regular ml-1">
                (optional)
              </span>
            )}
          </label>
        )}

        {/* Textarea element */}
        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          rows={rows}
          value={value}
          maxLength={maxLength}
          aria-invalid={isError}
          aria-describedby={hasHelperOrError ? helperId : undefined}
          className={cn(
            // Base styles
            "w-full rounded-(--input-radius) border transition-colors",
            "shadow-(--input-shadow)",
            // Outline setup for focus (doesn't affect layout)
            "outline-1 -outline-offset-1 outline-transparent",
            currentSize.textarea,
            resizeClasses[resize],
            
            // Default state
            "bg-(--input-bg) border-(--input-border) text-(--input-text)",
            
            // Placeholder
            isError
              ? "placeholder:text-(--input-text-placeholder-error)"
              : "placeholder:text-(--input-text-placeholder)",
            
            // Hover state (not disabled, not error)
            !isError && !disabled && "hover:border-(--input-border-hover)",
            
            // Focus state - uses outline to avoid layout shift
            !isError && "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-focus)",
            
            // Error state
            isError && "border-(--input-border-error)",
            isError && "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-error)",
            
            // Disabled state
            disabled && [
              "bg-(--input-bg-disabled)",
              "text-(--input-text-disabled)",
              "cursor-not-allowed",
              "pointer-events-none",
            ],
            
            className
          )}
          {...props}
        />

        {/* Character count */}
        {showCounter && (
          <p className={cn("body-xs-regular text-right", counterColor)}>
            {charCount}/{maxLength}
          </p>
        )}

        {/* Helper text / Error message */}
        {hasHelperOrError && (
          <p
            id={helperId}
            className={cn(
              "body-xs-regular",
              isError
                ? "text-(--input-helper-error)"
                : "text-(--input-helper)",
              disabled && "opacity-50"
            )}
          >
            {isError && errorMessage ? errorMessage : helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };

